import { useEffect, useMemo, useRef, useState } from 'react';

import { conversationOutcomeLabel, conversationStatusLabel } from '../../persons/persons-adapter';
import { dayDividerLabel, dayKey } from '../format';

import { CaptureLine } from './CaptureLine';
import { EpisodeRail, rotuloDoEpisodio } from './EpisodeRail';
import { MessageBubble } from './MessageBubble';
import { SystemEventCard } from './SystemEventCard';

import type { MessageItem } from '../../../lib/types';
import type { PersonConversation } from '../../persons/types';
import type { PendencyFixture } from '../hooks/use-panel-fixtures';

/**
 * MessageList — render do histórico (ordem cronológica, antiga → recente).
 *
 * Pagination reversa via `onLoadOlder` (carregando mais antigas no topo).
 * Estados de loading e empty tratados in-place.
 *
 * Task 53 — WhatsApp scroll pattern canonical:
 *   - Mount → auto-scroll bottom (última msg).
 *   - Nova msg + isAtBottom → segue mantendo bottom.
 *   - Nova msg + scroll acima → preserva posição + JumpToBottomButton com badge.
 *   - Click no botão OR scroll manual até bottom → contador zera + botão some.
 *   - Threshold canonical isAtBottom: diff < 100px.
 */

const AT_BOTTOM_THRESHOLD_PX = 100;

/** Linha de leitura do scrollspy: o que está a até 80px do topo é «o que leio». */
const EPISODE_SPY_OFFSET_PX = 80;

/**
 * Teto do «carregar até aqui». Cada página traz 30 mensagens, então 10 páginas
 * ≈ 300 mensagens — além disso a espera deixa de valer e é mais honesto abrir a
 * conversa direto (re-ancorando) do que puxar meio histórico em silêncio.
 */
const MAX_JUMP_PAGES = 10;

/**
 * Margem do carregamento por rolagem: a sentinela dispara 300px ANTES do topo,
 * para o histórico chegar enquanto o operador ainda rola. Sem margem, a rolagem
 * encosta no fim do conteúdo e só então espera.
 */
const AUTOLOAD_MARGEM_PX = 300;

/**
 * Subtítulo do cartão de fronteira — o «como encerrou» do mockup 03/1a.
 *
 * O mockup escreve «conversa encerrada pela IA»; nós não temos o ATOR do
 * encerramento no wire da ficha, e inventá-lo seria pior que omitir. Usamos o
 * STATUS, que é dado real e responde à mesma pergunta («como terminou») — e é
 * ortogonal ao chip, que mostra o DESFECHO (podem divergir: desfecho resolvido
 * com status arquivada).
 */
function episodeClosingLabel(episode: { status: string }): string {
  return `conversa ${conversationStatusLabel(episode.status).toLowerCase()}`;
}

export function MessageList({
  messages,
  hasMore,
  isFetchingMore,
  isLoading,
  onLoadOlder,
  onRetry,
  currentConversationId,
  personScope,
  capturedPendencies,
}: {
  messages: MessageItem[];
  hasMore: boolean;
  isFetchingMore: boolean;
  isLoading: boolean;
  onLoadOlder: () => void;
  onRetry?: (optimisticId: string) => void;
  /** B2 — id da conversa CORRENTE (rotula a divisória de episódio). */
  currentConversationId?: string;
  /**
   * Spec 2026-08-14 §5.1 — pendências que NASCERAM de uma fala deste fio. A lista já
   * é buscada pelo painel direito; aqui ela só é indexada por `sourceMessageId` para
   * desenhar a linha de captura sob a mensagem certa. Zero request novo: o React
   * Query compartilha o cache pela mesma queryKey.
   */
  capturedPendencies?: PendencyFixture[];
  /**
   * B2 (spec 2026-08-12 §3.4) — controle do transcript contínuo por pessoa.
   * `active` = pedido pelo operador; `applied` = o servidor CONFIRMOU (a
   * verdade vem do meta — flag/cap/regras duras podem degradar).
   *
   * Alvo D round2: `totalCount`/`episodes` (endpoint da ficha, cache
   * compartilhado) alimentam o banner, a divisória-cartão e a trilha lateral;
   * `onOpenEpisode` navega quando o episódio não está carregado no fio.
   */
  personScope?: {
    active: boolean;
    applied: boolean;
    onToggle: () => void;
    totalCount?: number;
    episodes?: PersonConversation[];
    onOpenEpisode?: (conversationId: string) => void;
  };
}): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Sentinela do topo — o que dispara o carregamento por rolagem. */
  const sentinelaRef = useRef<HTMLDivElement>(null);
  const lastSeenMsgId = useRef<string | null>(null);
  const didInitialScroll = useRef(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  /** Episódio VISÍVEL no fio (scrollspy) — não confundir com o ABERTO. */
  const [visibleEpisodeId, setVisibleEpisodeId] = useState<string | null>(null);
  /**
   * Episódio que o operador DECLAROU querer ver, clicando na trilha.
   *
   * Existe porque a marca era inferida só da geometria, e isso erra depois de um
   * clique: se a rolagem não consegue levar o alvo até a linha de leitura
   * (episódio perto do fim do fio, ou conteúdo insuficiente abaixo), a última
   * fronteira acima da linha é a ANTERIOR — e a marca caía no episódio errado. O
   * TL mediu isso na tela: «cliquei no dia sete e ele marcou do dia quatro; cliquei
   * de novo no sete e aí veio».
   *
   * Clicar é uma declaração, não uma dica. Ela prevalece até se cumprir — e se
   * cumpre sozinha quando o alvo alcança a linha, sem timer.
   */
  const intencaoDeLeitura = useRef<string | null>(null);
  /** Salto em curso: pagina para trás até o episódio alvo aparecer. */
  const [pendingJump, setPendingJump] = useState<{
    conversationId: string;
    pages: number;
    /**
     * Id da mensagem mais antiga quando a última página foi PEDIDA. Só pedimos
     * a próxima depois que este valor muda — isto é, depois que página nova
     * realmente chegou. Sem essa trava, um pedido que não devolve nada faz o
     * efeito re-disparar e queimar o teto inteiro num piscar (medido no teste:
     * as 10 páginas de uma vez, terminando em navegação indevida).
     */
    oldestIdNoPedido: string | null;
  } | null>(null);

  // Last message canonical para detectar msg nova — usar ID em vez de
  // length porque `useMessages` tem LIMIT canonical (paginação reversa).
  // Quando msg nova chega + invalidate → refetch traz mesmas N msgs (uma
  // antiga sai, nova entra) → length NÃO MUDA → bug canonical pré-fix
  // não detectava msg nova. Comparar lastId é robusto canonical.
  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastMsgId = lastMsg?.id ?? null;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || lastMsgId === null) return;

    // Race condition canonical fix: usar requestAnimationFrame para garantir
    // DOM atualizado (bubble da msg nova renderizada) antes de calcular
    // scrollHeight novo.
    const scrollToBottomCanonical = (): void => {
      const cur = scrollRef.current;
      if (cur) cur.scrollTop = cur.scrollHeight;
    };

    if (!didInitialScroll.current) {
      requestAnimationFrame(scrollToBottomCanonical);
      didInitialScroll.current = true;
      lastSeenMsgId.current = lastMsgId;
      return;
    }

    // Msg nova canonical detectada quando lastMsgId mudou. Como `useMessages`
    // tem LIMIT, msg nova substitui mais antiga (length constante) — comparar
    // por ID canonical é necessário.
    const isNewMessage = lastSeenMsgId.current !== lastMsgId;
    if (isNewMessage) {
      if (isAtBottom) {
        requestAnimationFrame(scrollToBottomCanonical);
      } else {
        setUnreadCount((c) => c + 1);
      }
      lastSeenMsgId.current = lastMsgId;
    }
  }, [lastMsgId, isAtBottom]);

  /**
   * Scrollspy — qual episódio o operador está LENDO agora.
   *
   * Lê as fronteiras (`data-episode-start`) e escolhe a última cujo topo já
   * passou da linha de leitura. Cálculo direto em vez de IntersectionObserver:
   * é determinístico (não depende de ordem de callback), roda no mesmo evento
   * que já tratamos e não precisa de re-observar a cada página carregada.
   */
  const computeVisibleEpisode = (): void => {
    const el = scrollRef.current;
    if (el === null) return;
    const linhaDeLeitura = el.getBoundingClientRect().top + EPISODE_SPY_OFFSET_PX;
    let atual: string | null = null;
    for (const marca of el.querySelectorAll('[data-episode-start]')) {
      if (marca.getBoundingClientRect().top <= linhaDeLeitura) {
        atual = marca.getAttribute('data-episode-start');
      }
    }
    // Há intenção declarada por clique? Ela vence a inferência até se cumprir.
    // «Cumprir-se» = a geometria concordar com ela; aí a rolagem livre reassume,
    // e nenhum temporizador participa da decisão.
    const intencao = intencaoDeLeitura.current;
    if (intencao !== null) {
      if (atual === intencao) intencaoDeLeitura.current = null;
      else {
        setVisibleEpisodeId(intencao);
        return;
      }
    }
    setVisibleEpisodeId(atual);
  };

  const handleScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const diff = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = diff < AT_BOTTOM_THRESHOLD_PX;
    setIsAtBottom(atBottom);
    if (atBottom && unreadCount > 0) setUnreadCount(0);
    computeVisibleEpisode();
  };

  const jumpToBottom = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setUnreadCount(0);
  };

  /**
   * Motor do «carregar até aqui»: enquanto houver salto pendente, pagina para
   * trás até a fronteira do alvo existir no DOM; então rola até ela. Desiste
   * (e navega, re-ancorando) quando o fio acaba ou o teto é atingido —
   * nunca fica puxando página em silêncio.
   */
  useEffect(() => {
    if (pendingJump === null) return;

    const alvo = scrollRef.current?.querySelector(
      `[data-episode-start="${pendingJump.conversationId}"]`,
    );
    if (alvo !== null && alvo !== undefined) {
      // POUSO INSTANTÂNEO, e é uma decisão de correção — não de gosto. Com
      // `smooth` abria-se uma corrida: a animação calcula o `scrollTop` de
      // destino AGORA e leva ~300ms; a trava cai na linha seguinte; a sentinela
      // do carregamento-por-rolagem volta a valer com o alvo indo para o topo,
      // dispara, e mais uma página entra ACIMA. A ancoragem nativa não corrige
      // rolagem programática em curso, então a animação pousava no número
      // velho — mostrando o que estava ANTES do alvo. Foi o defeito que o TL
      // mediu: «cliquei na do dia três e ele pulou mais duas pra cima».
      // `auto` é síncrono: quando retorna, não há janela para a corrida.
      alvo.scrollIntoView({ behavior: 'auto', block: 'start' });
      setPendingJump(null);
      return;
    }
    if (isFetchingMore) return;
    // Página pedida e nada novo chegou ainda — espera, não pede de novo.
    const oldestId = messages[0]?.id ?? null;
    if (pendingJump.pages > 0 && oldestId === pendingJump.oldestIdNoPedido) return;

    if (!hasMore || pendingJump.pages >= MAX_JUMP_PAGES) {
      const destino = pendingJump.conversationId;
      setPendingJump(null);
      personScope?.onOpenEpisode?.(destino);
      return;
    }
    setPendingJump({
      conversationId: pendingJump.conversationId,
      pages: pendingJump.pages + 1,
      oldestIdNoPedido: oldestId,
    });
    onLoadOlder();
    // NOTA: `onLoadOlder`/`personScope` ficam FORA das deps de proposito — sao
    // recriados a cada render do pai e re-disparariam o efeito sem o estado
    // ter mudado.
  }, [messages, pendingJump, isFetchingMore, hasMore]);

  // Recalcula o episódio visível quando chegam páginas novas (o conteúdo
  // acima do cursor muda de posição sem que haja evento de scroll).
  useEffect(() => {
    computeVisibleEpisode();
  }, [messages]);

  /**
   * A rolagem carrega o histórico (spec `2026-08-13-rolagem-carrega-historico`).
   *
   * O operador sobe e as mensagens anteriores chegam — sem clique. Isto só é
   * seguro porque a âncora nativa de rolagem preserva a posição de leitura:
   * medido no browser em 13/Ago, 2762px inseridos no topo deslocaram a leitura
   * em 23px. Como o conteúdo novo entra ACIMA da sentinela, ela sai de vista
   * sozinha depois de cada carga — dá «uma página por subida», sem contador.
   */
  const oldestIdNoAutoPedido = useRef<string | null>(null);
  const [autoloadTravado, setAutoloadTravado] = useState(false);
  // `onLoadOlder` é recriado a cada render do pai. Guardado em ref, o observer
  // não precisa dele nas deps (ref é estável) e não se re-cria a cada render —
  // resolve pela estrutura o que o efeito do salto resolve por NOTA.
  const onLoadOlderRef = useRef(onLoadOlder);
  useEffect(() => {
    onLoadOlderRef.current = onLoadOlder;
  }, [onLoadOlder]);
  const temObserver = typeof IntersectionObserver !== 'undefined';
  const primeiroIdCarregado = messages[0]?.id ?? null;
  const saltoEmCurso = pendingJump !== null;

  useEffect(() => {
    if (!temObserver) return;
    const sentinela = sentinelaRef.current;
    const raiz = scrollRef.current;
    if (sentinela === null || raiz === null) return;

    const obs = new IntersectionObserver(
      (entradas) => {
        if (!entradas.some((e) => e.isIntersecting)) return;
        // TRAVA 1 — busca em curso: cada quadro de rolagem empilharia um pedido.
        // TRAVA 2 — o motor do «carregar até aqui» já pagina; dois motores
        // juntos queimam o teto de 10 páginas (defeito medido no PR #532). Vale
        // como cinto duplo: a sentinela nem é renderizada durante o salto.
        if (isFetchingMore || saltoEmCurso || !hasMore) return;
        // TRAVA 3 — o cinto de segurança: se a ancoragem não compensar em algum
        // estado e a sentinela ficar visível após a carga, o laço NÃO martela a
        // API. Ele para, porque o topo não mudou — e a UI devolve o botão.
        if (primeiroIdCarregado !== null && primeiroIdCarregado === oldestIdNoAutoPedido.current) {
          setAutoloadTravado(true);
          return;
        }
        oldestIdNoAutoPedido.current = primeiroIdCarregado;
        setAutoloadTravado(false);
        onLoadOlderRef.current();
      },
      { root: raiz, threshold: 0, rootMargin: `${AUTOLOAD_MARGEM_PX.toString()}px 0px 0px 0px` },
    );
    obs.observe(sentinela);
    return () => {
      obs.disconnect();
    };
  }, [temObserver, isFetchingMore, saltoEmCurso, hasMore, primeiroIdCarregado]);

  /**
   * O botão é FALLBACK (decisão do TL, 13/Ago): sai do caminho normal e volta
   * só quando o carregamento por rolagem não vai acontecer — navegador sem
   * `IntersectionObserver`, ou TRAVA 3 disparada. Quando aparece, aparece para
   * todo mundo; nunca vira botão invisível.
   */
  const mostrarBotaoFallback = !temObserver || autoloadTravado;

  // Alvo D — mapa conversationId→episódio para a divisória-cartão (o dado é
  // o do endpoint da ficha; sem ele, a divisória degrada à linha simples).
  const episodesById = useMemo(() => {
    const map = new Map<string, PersonConversation>();
    for (const e of personScope?.episodes ?? []) map.set(e.id, e);
    return map;
  }, [personScope?.episodes]);

  /**
   * O episódio que a oferta de consulta aponta: o MARCADO na trilha, quando ele
   * não é a conversa da rota. Nulo na conversa aberta — ali «ver só esta» segue
   * com o sentido de sempre (desligar o fio), porque o referente é o mesmo.
   */
  const episodioEmFoco =
    visibleEpisodeId !== null && visibleEpisodeId !== currentConversationId
      ? episodesById.get(visibleEpisodeId)
      : undefined;

  const jumpToEpisode = (conversationId: string): void => {
    // A DECLARAÇÃO vem antes de qualquer rolagem: mesmo que o scroll não alcance
    // (alvo perto do fim, ou paginação em curso), a trilha já mostra onde o
    // operador escolheu estar. A conversa ABERTA é exceção — ela tem a marca
    // forte, e a fraca na mesma linha faria a mais fraca vencer.
    if (conversationId !== currentConversationId) {
      intencaoDeLeitura.current = conversationId;
      setVisibleEpisodeId(conversationId);
    } else {
      intencaoDeLeitura.current = null;
    }

    const el = scrollRef.current?.querySelector(`[data-episode-start="${conversationId}"]`);
    if (el !== null && el !== undefined) {
      // Mesma regra do pouso do motor de paginação, e pelo mesmo motivo: o alvo
      // já carregado perto do topo sofre a mesma corrida com a sentinela. Uma
      // regra só, sem exceção para raciocinar depois.
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      return;
    }
    // Episódio ainda não carregado. Se ele é MAIS ANTIGO que a janela atual, o
    // fio alcança por paginação — «carregar até aqui» —, mantendo UMA timeline
    // contínua em vez de re-ancorar e descartar o que já estava carregado
    // (feedback do TL: saltar da 8ª para a 3ª quebrava o fio). Se é mais NOVO
    // que a âncora, ele não pertence a esta janela: navega e re-ancora.
    const alvo = episodesById.get(conversationId);
    const maisAntigaCarregada = messages[0];
    // Compara o INÍCIO do episódio (`createdAt`) com a mensagem mais antiga
    // carregada. Era `updatedAt`, e isso punha dois RELÓGIOS DIFERENTES na mesma
    // conta: `updatedAt` é de mutação (arquivar move), então um episódio antigo
    // recém-arquivado media como «não é mais antigo» e caía no ramo de NAVEGAR —
    // a rota trocava, o fio remontava e rolava pro fim. Medido em 13/Ago.
    const éMaisAntigo =
      alvo !== undefined &&
      maisAntigaCarregada !== undefined &&
      new Date(alvo.createdAt).getTime() < new Date(maisAntigaCarregada.sentAt).getTime();

    if (éMaisAntigo && hasMore) {
      // Só ARMA o salto — quem pede as páginas é o efeito, via única (pedir
      // aqui também disparava duas requisições para a primeira página).
      setPendingJump({ conversationId, pages: 0, oldestIdNoPedido: null });
      return;
    }
    personScope?.onOpenEpisode?.(conversationId);
  };

  if (isLoading) {
    return (
      <div className="flex-1 p-6 text-sm text-text-secondary" aria-busy="true">
        Carregando mensagens…
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-text-secondary">
        Sem mensagens nesta conversa ainda.
      </div>
    );
  }
  const railEpisodes = personScope?.episodes ?? [];
  const showRail =
    personScope !== undefined &&
    personScope.active &&
    personScope.applied &&
    railEpisodes.length > 1;

  return (
    // `min-w-0` nos DOIS níveis é contenção, não estilo: item de flex nasce com
    // `min-width: auto` e se recusa a encolher abaixo do próprio `min-content`.
    // O cartão de divisória tem texto `truncate` (`nowrap`), cujo `min-content`
    // é a frase inteira — medido em 819px. Sem estes dois `min-w-0`, a coluna
    // inflava a 913px e pintava 189px POR CIMA do Resumo vivo (medição viva de
    // 13/Ago; guard em `MessageList.layout.spec.tsx`).
    <div className="flex-1 relative flex min-h-0 min-w-0">
      {/* Alvo D — trilha lateral de episódios (nasce RECOLHIDA — decisão 3). */}
      {showRail && (
        <EpisodeRail
          episodes={railEpisodes}
          {...(personScope.totalCount !== undefined && { totalCount: personScope.totalCount })}
          {...(currentConversationId !== undefined && { currentConversationId })}
          {...(visibleEpisodeId !== null && { visibleConversationId: visibleEpisodeId })}
          onJump={jumpToEpisode}
        />
      )}
      <div className="flex-1 relative flex flex-col min-h-0 min-w-0">
        {/* Alvo D — fio LIGADO por padrão: banner FORA do scroll (sempre
            visível — F.5 M2: o pouso é no bottom; o opt-out não pode morar
            só no topo do histórico) com o total real e o «ver só esta». */}
        {personScope !== undefined && personScope.active && personScope.applied && (
          <div
            data-testid="fio-banner"
            className="flex items-center gap-2 px-4 py-1.5 text-xs"
            style={{ background: 'var(--brand-subtle)', color: 'var(--brand-subtle-fg)' }}
          >
            <span aria-hidden>⚡</span>
            <span>
              Fio completo do cliente
              {personScope.totalCount !== undefined && (
                <>
                  {' '}
                  — <strong>{personScope.totalCount} conversas</strong>
                </>
              )}
            </span>
            <span className="flex-1" />
            {/*
             * A oferta NOMEIA o alvo. «ver só esta conversa» sempre significou a
             * conversa da ROTA — e quando o operador está lendo um episódio
             * antigo no fio, «esta» passa a ser ambíguo: o referente andaria com
             * a barra de rolagem. Nomeando (decisão do TL, 13/Ago: data mais o
             * assunto, «só a data é simplório demais»), o botão diz para onde
             * leva antes do clique, e a marca da trilha confirma.
             *
             * Consultar um episódio antigo é NAVEGAR até ele — a página de
             * conversa já sabe se comportar em estado terminal (composer em
             * leitura, cabeçalho próprio). Não se inventa um estado híbrido.
             */}
            {episodioEmFoco !== undefined ? (
              <button
                type="button"
                data-testid="consultar-episodio"
                onClick={() => personScope.onOpenEpisode?.(episodioEmFoco.id)}
                className="underline"
              >
                ver só a de {rotuloDoEpisodio(episodioEmFoco)} ↗
              </button>
            ) : (
              <button
                type="button"
                data-testid="person-scope-toggle"
                onClick={personScope.onToggle}
                className="underline"
              >
                ver só esta conversa
              </button>
            )}
          </div>
        )}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-auto px-6 py-4 flex flex-col"
          data-testid="message-list-scroll"
        >
          {personScope !== undefined && (!personScope.active || !personScope.applied) && (
            <div className="flex flex-col items-center gap-1 mb-2">
              <button
                type="button"
                data-testid="person-scope-toggle"
                onClick={personScope.onToggle}
                className="text-xs text-text-secondary hover:text-text-primary underline"
              >
                {personScope.active
                  ? 'Ocultar conversas anteriores'
                  : /* Era «Ver conversas anteriores desta pessoa no fio». Estava
                       errado quando o operador está numa conversa PASSADA: existem
                       também conversas posteriores a ela, e o fio traz as duas
                       pontas (correção do TL, 13/Ago). */
                    'Ver o fio completo desta pessoa'}
              </button>
              {personScope.active && !personScope.applied && !isLoading && (
                <span className="text-2xs text-text-secondary italic">
                  Histórico contínuo indisponível para esta conversa.
                </span>
              )}
            </div>
          )}
          {pendingJump !== null && (
            <div
              data-testid="episode-jump-progress"
              role="status"
              className="flex justify-center mb-3"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-3)' }}
            >
              Carregando conversas anteriores… {pendingJump.pages} de {MAX_JUMP_PAGES}
            </div>
          )}
          {hasMore && pendingJump === null && (
            <>
              {/* Sentinela: 1px, sem texto, fora da ordem de leitura. Não é
                  conteúdo — é o gatilho da rolagem. **1px e não 0**: alvo de
                  área ZERO é caso de borda com histórico de divergência entre
                  navegadores no `IntersectionObserver` (a interseção de área nula
                  pode não contar como «intersecting» com threshold 0). 1px custa
                  nada visualmente e tira a dúvida. */}
              <div
                ref={sentinelaRef}
                data-testid="fio-sentinela"
                aria-hidden
                style={{ height: 1 }}
              />
              {isFetchingMore ? (
                <div
                  data-testid="fio-autoload-status"
                  role="status"
                  className="flex justify-center mb-3"
                  style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-3)' }}
                >
                  Carregando conversas anteriores…
                </div>
              ) : (
                mostrarBotaoFallback && (
                  <div className="flex justify-center mb-3">
                    <button
                      type="button"
                      onClick={onLoadOlder}
                      className="text-xs text-text-secondary hover:text-text-primary underline"
                    >
                      Carregar mensagens mais antigas
                    </button>
                  </div>
                )
              )}
            </>
          )}
          <div className="flex flex-col gap-0.5">
            {messages.map((m, i) => {
              // A promessa capturada desta fala, se houver. `find` (e não um Map
              // memoizado) porque a lista tem ordem de unidades por conversa — o
              // custo é irrelevante e evita um segundo lugar guardando estado.
              const captured = capturedPendencies?.find((p) => p.sourceMessageId === m.id);
              // B2 — divisória de episódio: fronteira quando o conversationId
              // muda entre mensagens consecutivas (só existe no escopo-pessoa;
              // na lista clássica todas pertencem à mesma conversa — inerte).
              // LIMITAÇÃO DECLARADA (F.5 r4): pessoa com 2+ conversas ATIVAS em
              // paralelo intercala mensagens no sort global e a divisória dispara
              // a cada alternância — fio picotado. Registrada na spec §3.4;
              // tratamento (segmentar trechos contíguos) = follow-up com design.
              const prev = i > 0 ? messages[i - 1] : undefined;
              const crossesEpisode = prev !== undefined && prev.conversationId !== m.conversationId;
              // Divisória de DIA — o rótulo que tirou a data de dentro do balão
              // (decisão do TL, 13/Ago). Sem ela, cada balão repetiria a data;
              // com ela, o balão só mostra a hora e o intervalo entre as falas
              // fica legível. Na fronteira de episódio a divisória-cartão já
              // carrega a data — não duplicamos.
              const crossesDay =
                !crossesEpisode && (prev === undefined || dayKey(prev.sentAt) !== dayKey(m.sentAt));
              const dayNode = crossesDay ? (
                <div
                  data-testid="day-divider"
                  className="flex items-center"
                  style={{ gap: 10, margin: '6px 0' }}
                >
                  <span
                    className="flex-1"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                    aria-hidden
                  />
                  <span
                    className="uppercase"
                    style={{
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--fg-4)',
                      letterSpacing: 'var(--track-caps)',
                    }}
                  >
                    {dayDividerLabel(m.sentAt)}
                  </span>
                  <span
                    className="flex-1"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                    aria-hidden
                  />
                </div>
              ) : null;
              const bubble =
                m.origin === 'system' ? (
                  <SystemEventCard message={m} />
                ) : (
                  <MessageBubble message={m} {...(onRetry !== undefined && { onRetry })} />
                );
              const node =
                captured === undefined ? (
                  bubble
                ) : (
                  <>
                    {bubble}
                    <CaptureLine pendency={captured} />
                  </>
                );
              if (!crossesEpisode) {
                // A PRIMEIRA mensagem carregada também é fronteira de episódio
                // (não há `prev` para cruzar): sem este marcador, o clique na
                // trilha no episódio do topo não tinha alvo de scroll e caía no
                // fallback de navegação.
                return i === 0 ? (
                  <div key={m.id} data-episode-start={m.conversationId}>
                    {dayNode}
                    {node}
                  </div>
                ) : (
                  <div key={m.id}>
                    {dayNode}
                    {node}
                  </div>
                );
              }
              const entrandoNaCorrente = m.conversationId === currentConversationId;
              const dataEpisodio = new Date(m.sentAt).toLocaleDateString('pt-BR');
              // Alvo D — divisória-cartão (mockup 03/1a): fronteira de episódio
              // antigo com chip de desfecho + título do LivingSummary. Sem dado
              // do episódio (endpoint da ficha não alcançou): linha simples.
              const episode = entrandoNaCorrente ? undefined : episodesById.get(m.conversationId);
              return (
                <div key={m.id} data-episode-start={m.conversationId}>
                  {episode !== undefined ? (
                    // Anatomia FIEL ao mockup 03/1a (`.epi` + `.epicard`): réguas
                    // `--border-default` · gap 10px · margem 6px · cartão a 90% ·
                    // chip de desfecho · TÍTULO em `--text-xs`/semibold/`--fg-2` ·
                    // subtítulo «data · como encerrou» em `--text-2xs`/`--fg-3`.
                    // A versão anterior herdava o tamanho do stream (a classe
                    // `text-2xs` não existe — Item 436) e perdia o «como encerrou».
                    <div
                      data-testid="episode-divider"
                      className="flex items-center"
                      style={{ gap: 10, margin: '6px 0' }}
                    >
                      <span
                        className="flex-1"
                        style={{ borderTop: '1px solid var(--border-default)' }}
                        aria-hidden
                      />
                      <div
                        data-testid="episode-divider-card"
                        className="flex items-center"
                        style={{
                          gap: 10,
                          // O mockup usa 90% com título CURTO; com o resumo real
                          // (frase inteira) o cartão comia as duas réguas e a
                          // anatomia «cartão centrado entre linhas» sumia. Teto
                          // absoluto preserva o desenho com dado de verdade.
                          maxWidth: 'min(90%, 520px)',
                          // `max-width` limita o teto, NÃO o piso: sem este
                          // `minWidth: 0` o `truncate` do título mantém o texto
                          // inteiro como largura mínima e o cartão volta a
                          // empurrar a coluna (era 819px de piso — 13/Ago).
                          minWidth: 0,
                          padding: '8px 12px',
                          border: '1px solid var(--border-default)',
                          background: 'var(--bg-surface-2)',
                          borderRadius: 'var(--radius-lg)',
                        }}
                      >
                        {episode.outcomeType !== undefined && episode.outcomeType !== null && (
                          <span
                            style={{
                              flex: 'none',
                              fontSize: 'var(--text-2xs)',
                              fontWeight: 'var(--fw-medium)',
                              borderRadius: 'var(--radius-sm)',
                              padding: '2px 6px',
                              // F.5 M3 — língua de cor única: verde SÓ resolvida.
                              ...(episode.outcomeType === 'resolved'
                                ? { background: 'var(--success-bg)', color: 'var(--success-fg)' }
                                : { background: 'var(--bg-subtle)', color: 'var(--fg-3)' }),
                            }}
                          >
                            {conversationOutcomeLabel(episode.outcomeType)}
                          </span>
                        )}
                        <span className="flex flex-col" style={{ minWidth: 0 }}>
                          {episode.excerpt !== undefined && episode.excerpt !== null && (
                            <span
                              className="truncate"
                              style={{
                                fontSize: 'var(--text-xs)',
                                fontWeight: 'var(--fw-semibold)',
                                color: 'var(--fg-2)',
                              }}
                            >
                              {episode.excerpt}
                            </span>
                          )}
                          <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-3)' }}>
                            {dataEpisodio} · {episodeClosingLabel(episode)}
                          </span>
                        </span>
                      </div>
                      <span
                        className="flex-1"
                        style={{ borderTop: '1px solid var(--border-default)' }}
                        aria-hidden
                      />
                    </div>
                  ) : (
                    // `.epi` + `.epi .tag` do mockup: mesma régua, rótulo em
                    // `--text-2xs`/`--fg-3` explícito (a classe `text-2xs` é
                    // no-op — Item 436 — e o rótulo herdava o corpo do stream).
                    <div
                      data-testid="episode-divider"
                      className="flex items-center"
                      style={{ gap: 10, margin: '6px 0' }}
                    >
                      <span
                        className="flex-1"
                        style={{ borderTop: '1px solid var(--border-default)' }}
                        aria-hidden
                      />
                      <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-3)' }}>
                        {entrandoNaCorrente
                          ? `Início desta conversa · ${dataEpisodio}`
                          : `Conversa anterior · ${dataEpisodio}`}
                      </span>
                      <span
                        className="flex-1"
                        style={{ borderTop: '1px solid var(--border-default)' }}
                        aria-hidden
                      />
                    </div>
                  )}
                  {node}
                </div>
              );
            })}
          </div>
        </div>
        {!isAtBottom && (
          <button
            type="button"
            onClick={jumpToBottom}
            data-testid="jump-to-bottom"
            className="absolute right-4 bottom-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface border border-border-default shadow-md text-xs text-text-primary hover:bg-bg-canvas transition"
            aria-label={
              unreadCount > 0
                ? `Ir para a última mensagem (${unreadCount.toString()} não lidas)`
                : 'Ir para a última mensagem'
            }
            title="Ir para a última mensagem"
          >
            <span aria-hidden>↓</span>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-2xs font-semibold"
                style={{ background: 'var(--brand)', color: 'var(--fg-on-accent)' }}
              >
                {unreadCount}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
